export type AppleIdMatchResult = 'unchecked' | 'checking' | 'matched' | 'mismatch' | 'failed';

export interface AssignmentDTO {
  source?: string;
  agent_id?: number;
  agent_name?: string;
  agent_portal_task_id?: number;
  agent_portal_revision_id?: number;
  claim_snapshot?: { revision_id?: number; source?: string; agent_id?: number; agent_portal_task_id?: number; reward_amount_minor?: number; };
  attempt_no?: number;

  query_keyword?: string;
  id: number;
  task_id: number;
  task_title: string;
  task_status?: string;
  task_market_id?: string;
  user_id: number;
  user_nickname: string;
  user_email?: string;
  user_apple_account?: string;
  latest_app_version?: string;
  apple_id_match_result?: AppleIdMatchResult;
  status: string;
  evidence: string;
  review_note: string;
  task_type?: string;
  platform?: string;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  reviewed_at?: string;
  reviewer_id?: number;
  assign_ip?: string;
  submit_ip?: string;
  message_task?: MessageAssignmentTaskDTO | null;
  message_evidence?: MessageEvidenceDTO[] | null;
  message_review?: MessageAssignmentReviewDTO | null;
}

export interface MessageAssignmentTaskDTO {
  recipient_type?: string;
  target_account?: string;
  target_content?: string;
}

export interface MessageAssignmentReviewDTO {
  job_status?: string;
  recommendation?: string;
  recipient_matches?: boolean;
  body_matches?: boolean;
  reason_codes?: string[] | null;
}

export interface AssignmentListResponseDTO {
  total: number;
  page: number;
  page_size: number;
  items: AssignmentDTO[];
}

export interface ReviewResultDTO {
  id: number;
  status: string;
  reward_settled: boolean;
  reward_amount_fen: number;
  reward_amount_yuan: string;
  assignment_id?: number;
  reward_amount?: number;
}

// --- Message-send review DTOs ---

export type MessageReviewRecommendation = 'pass_candidate' | 'fail_candidate' | 'manual_required';

export type MessageReviewJobStatus =
  | 'not_started'
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'manual_required'
  | 'failed';

export interface MessageReviewDetailDTO {
  item: AssignmentDetailDTO;
  message_target?: MessageTargetDTO | null;
  session_timeline?: MessageEventDTO[] | null;
  app_attest?: MessageAppAttestDTO | null;
  evidence?: MessageEvidenceDTO[] | null;
  recommendation?: MessageRecommendationDTO | string | null;
  risk?: MessageRiskDTO | null;
  review_job?: MessageReviewJobDTO | null;
  override?: MessageOverrideDTO | null;
}

export interface MessageTargetDTO {
  item_id?: number;
  recipient_type?: string;
  target_account?: string;
  target_content?: string;
  item_status?: string;
  session_id?: string;
  session_status?: string;
  session_attempt_no?: number;
  assigned_at?: string;
  session_created_at?: string;
}

export interface MessageEventDTO {
  id: number;
  session_id: string;
  event_id: string;
  challenge_id?: string;
  event_type: string;
  native_result: string;
  state_before: string;
  state_after: string;
  result_code: string;
  created_at: string;
}

export interface MessageAppAttestDTO {
  keys?: MessageAppAttestKeyDTO[] | null;
  challenges?: MessageAppAttestChallengeDTO[] | null;
}

export interface MessageAppAttestKeyDTO {
  key_id: string;
  status: string;
  counter: number;
  last_used_at?: string;
}

export interface MessageAppAttestChallengeDTO {
  id: string;
  purpose: string;
  expires_at: string;
  used_at?: string;
  created_at: string;
}

export interface MessageEvidenceDTO {
  url?: string;
  id: number;
  session_id: string;
  position_no: number;
  mime_type: string;
  size_bytes: number;
  width: number;
  height: number;
  md5_hash: string;
  sha256: string;
  perceptual_hash: string;
  status: string;
  reason_code: string;
  created_at: string;
  updated_at: string;
  content_available?: boolean;
}

export interface MessageRecommendationDTO {
  status: MessageReviewRecommendation;
  provider?: string;
  model?: string;
  reasoning_effort?: string;
  config_version?: string;
  prompt_version?: string;
  provider_request_id?: string;
  created_at?: string;
}

export interface MessageRiskDTO {
  reason_codes?: string[] | null;
  recipient_matches?: boolean;
  body_matches?: boolean;
  messages_screen?: boolean;
  blue_imessage_detected?: boolean;
  redacted_summary?: MessageRedactedSummaryDTO | Record<string, unknown> | null;
}

export interface MessageRedactedSummaryDTO {
  recipient?: string;
  recipient_mask?: string;
  recipient_summary?: string;
  body?: string;
  body_mask?: string;
  body_summary?: string;
  extracted_channel?: string;
  message_state?: string;
  recipient_visibility?: string;
  recipient_completeness?: string;
  body_completeness?: string;
  body_truncated?: boolean;
  body_obscured?: boolean;
  confidence?: number;
}

export interface MessageReviewJobDTO {
  id?: number;
  session_id?: string;
  status: MessageReviewJobStatus | string;
  available_at?: string;
  attempt_count?: number;
  max_attempts?: number;
  last_error_code?: string;
}

export interface MessageOverrideDTO {
  can_override: boolean;
  required_permission: string;
  reason_required?: boolean;
}

export interface MessageReviewRetryResultDTO {
  job_id: number;
  assignment_id: number;
  session_id: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
}

export interface MessageItemReplaceResultDTO {
  assignment_id: number;
  old_item_id: number;
  new_item_id: number;
  status: string;
}

// --- Download-specific DTOs ---

export interface DownloadAppDTO {
  id: number;
  bundle_id: string;
  name: string;
  version: string;
  price: number;
  formatted_price: string;
  artwork_url: string;
  developer_name: string;
  description: string;
  genre: string;
  file_size_bytes: number;
  rating: number;
  rating_count: number;
  content_rating: string;
  screenshot_urls: string[];
}

export interface DownloadEvidenceDTO {
  bundle_id: string;
  app_version: string;
  file_size_bytes: number;
  md5_hash: string;
  device_guid: string;
  download_started_at: string;
  download_completed_at: string;
}

export interface DownloadRecordDTO {
  id?: number;
  assignment_id: number;
  status: string;
  downloaded_bytes?: number;
  total_bytes?: number;
  error_message?: string;
  device_guid?: string;
  apple_email?: string;
  bundle_id?: string;
  app_version?: string;
  file_size_bytes?: number;
  md5_hash?: string;
  source?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AssignmentOCRCheckDTO {
  id?: number;
  assignment_id: number;
  user_id: number;
  task_id: number;
  image_index: number;
  image_path: string;
  provider: string;
  request_id?: string;
  status: 'checking' | 'matched' | 'not_found' | 'mismatch' | 'failed' | 'skipped';
  extracted_accounts?: string[];
  text_lines?: string[];
  failure_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface AssignmentDetailDTO extends AssignmentDTO {
  download_app?: DownloadAppDTO | null;
  download_evidence?: DownloadEvidenceDTO | null;
  download_records?: DownloadRecordDTO[] | null;
  ocr_checks?: AssignmentOCRCheckDTO[] | null;
}

export interface DownloadStatsDTO {
  task_id: number;
  total_attempts: number;
  success_count: number;
  success_rate: number;
  pending_review_count: number;
  risk_review_required_count: number;
  failure_reasons?: DownloadStatsReasonDTO[] | Record<string, number> | null;
  risk_reasons?: DownloadStatsReasonDTO[] | Record<string, number> | null;
}

export interface DownloadStatsReasonDTO {
  code: string;
  count: number;
}

// --- Enums & Labels ---

export type AssignmentStatus = 'assigned' | 'submitted' | 'approved' | 'rejected' | 'settled' | 'expired';

export const ASSIGNMENT_STATUS_LABELS: Record<AssignmentStatus, string> = {
  assigned: '已领取',
  submitted: '待审核',
  approved: '已通过',
  rejected: '已拒绝',
  settled: '已结算',
  expired: '已超时',
};

export const ALL_ASSIGNMENT_STATUSES: AssignmentStatus[] = [
  'assigned',
  'submitted',
  'approved',
  'rejected',
  'settled',
  'expired',
];

export type DownloadRecordStatus = 'started' | 'downloading' | 'completed' | 'verified' | 'approved' | 'rejected';

export const DOWNLOAD_RECORD_STATUS_LABELS: Record<DownloadRecordStatus, string> = {
  started: '下载开始',
  downloading: '下载中',
  completed: '下载完成',
  verified: '自动验证通过',
  approved: '人工审核通过',
  rejected: '人工审核拒绝',
};

export const TASK_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '全部类型' },
  { value: 'normal', label: '普通任务' },
  { value: 'other_normal', label: '其他普通任务' },
  { value: 'download', label: '下载任务' },
  { value: 'message_send', label: '短信代发' },
];

export const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: '全部状态' },
  { value: 'submitted', label: '待审核' },
  { value: 'assigned', label: '已领取' },
  { value: 'approved', label: '已通过' },
  { value: 'rejected', label: '已拒绝' },
  { value: 'settled', label: '已结算' },
  { value: 'expired', label: '已超时' },
];

export const PLATFORM_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '全部平台' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' },
  { value: 'all', label: '全平台' },
];

export const APPLE_ID_MATCH_RESULT_LABELS: Record<AppleIdMatchResult, string> = {
  unchecked: '未校验',
  checking: '校验中',
  matched: '一致',
  mismatch: '不一致',
  failed: '识别失败',
};

export const APPLE_ID_MATCH_RESULT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '全部 Apple ID' },
  { value: 'checking', label: 'Apple ID 校验中' },
  { value: 'matched', label: 'Apple ID 一致' },
  { value: 'mismatch', label: 'Apple ID 不一致' },
  { value: 'failed', label: 'Apple ID 识别失败' },
];

export type AssignmentSortOrder = 'asc' | 'desc';

export const ASSIGNMENT_SORT_ORDER_OPTIONS: { value: AssignmentSortOrder; label: string }[] = [
  { value: 'asc', label: '提交时间升序' },
  { value: 'desc', label: '提交时间降序' },
];
